/**
 * Session detail tab (details).
 */
import * as React from 'react';
import { Clock, BookOpen, UserCircle, Video } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DetailsCard } from '@/components/common/DetailsCard';
import { ParticipantChip } from '@/components/common/ParticipantChip';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { Button } from '@/components/forms/Button';
import { JOINABLE_SESSION_STATUSES } from '@/components/sessions/session-configs';
import {
  calcDurationMinutes,
  formatSessionTime,
  getSessionFormatLabel,
  parseSessionGroupDate,
} from '@/components/sessions/inperson/session-table-utils';
import type { SessionWithParticipants } from '@/types/inperson/session.types';

/** Props for {@link SessionDetailsTab}. */
interface SessionDetailsTabProps {
  session: SessionWithParticipants;
  canJoinInformationMeeting: boolean;
  isJoining: boolean;
  onJoin: () => void;
}

/** Session detail tab (details). */
export const SessionDetailsTab: React.FC<SessionDetailsTabProps> = ({
  session,
  canJoinInformationMeeting,
  isJoining,
  onJoin,
}) => {
  const animators = session.facilitators || [];

  return (
    <div className="details-view-grid">
      <DetailsCard icon={Clock} title="Date et heure" colorName="color-2" colSpan={1}>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Date</p>
            <p className="text-base font-semibold text-foreground capitalize">
              {format(
                parseSessionGroupDate(String(session.sessionDate ?? '')),
                'EEEE d MMMM yyyy',
                {
                  locale: fr,
                },
              )}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Horaires</p>
            <div className="time-pill">
              <Clock className="w-4 h-4" />
              <span>
                {formatSessionTime(session.startTime)} - {formatSessionTime(session.endTime)}
              </span>
            </div>
          </div>

          {(session.durationMinutes ?? calcDurationMinutes(session.startTime, session.endTime)) !=
            null && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Durée</p>
              <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] text-[var(--color-1-nuance-1)] border border-[var(--color-1-nuance-1)]">
                {session.durationMinutes ?? calcDurationMinutes(session.startTime, session.endTime)}{' '}
                minutes
              </span>
            </div>
          )}
        </div>
      </DetailsCard>

      <DetailsCard icon={UserCircle} title="Configuration" colorName="color-1" colSpan={1}>
        <dl className="space-y-2 text-sm">
          {session.sessionFormat && (
            <div>
              <dt className="text-gray-600">Format</dt>
              <dd className="font-medium mt-0.5">{getSessionFormatLabel(session.sessionFormat)}</dd>
            </div>
          )}

          {animators.length > 0 && (
            <div>
              <dt className="text-gray-600 mb-1">Animateur(s)</dt>
              <dd className="flex flex-wrap gap-1">
                {animators.map((facilitator) => (
                  <ParticipantChip
                    key={facilitator.userId}
                    user={facilitator.user}
                    role="tutor"
                    size="sm"
                    showTooltip
                  />
                ))}
              </dd>
            </div>
          )}

          {canJoinInformationMeeting && JOINABLE_SESSION_STATUSES.has(session.status) && (
            <div>
              <dt className="text-gray-600">Lien visio</dt>
              <dd className="mt-1">
                <Button
                  variant="outline"
                  color="rgb(var(--color-error))"
                  size="icon"
                  loading={isJoining}
                  className={session.status === 'IN_PROGRESS' ? 'session-video-blink' : ''}
                  onClick={onJoin}
                  icon={Video}
                  tooltipText="Rejoindre réunion d'information"
                />
              </dd>
            </div>
          )}
        </dl>
      </DetailsCard>

      <DetailsCard icon={BookOpen} title="Notes internes" colorName="color-1" colSpan="full">
        {session.internalNotes ? (
          <div className="rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_5%,transparent)] p-4">
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {session.internalNotes}
            </p>
          </div>
        ) : (
          <EmptyPlaceholder message="Aucune note interne pour cette séance" />
        )}
      </DetailsCard>
    </div>
  );
};
